/**
 * Appearance: light/dark/system, and the accent color.
 *
 * Two things are true at once and the code has to respect both. Preferences
 * belong to the *account*, so they follow the user to a new device; but they
 * must be applied before the first paint, which is long before Firestore has
 * answered. So localStorage is the fast path and Firestore is the truth: the
 * inline script in index.html paints from the cache, this module reconciles
 * with the server when the user resolves, and writes go to both.
 *
 * The accent is a single seed. Every other shade is derived in CSS with
 * color-mix (see tokens.css), which is what stops a user-chosen color from
 * producing an incoherent or unreadable palette — it can move the hue, not
 * break the relationships.
 */

import type { ThemePreference, UserPrefs } from '../domain/types';

export const PREFS_KEY = 'ss.prefs.v2';

/**
 * The accents offered in the picker.
 *
 * Chosen to sit at a similar lightness and chroma so that whichever one is
 * picked, white text on it stays legible and the derived tints keep working in
 * both themes. A free color well is also allowed, but this is the shortcut
 * that always looks right.
 */
export const ACCENT_PRESETS: ReadonlyArray<{ name: string; value: string }> = [
  { name: 'Pine', value: '#1f6f4a' },
  { name: 'Ink', value: '#2f4a7c' },
  { name: 'Slate', value: '#43535e' },
  { name: 'Clay', value: '#a5533a' },
  { name: 'Ochre', value: '#8a6218' },
  { name: 'Plum', value: '#6b3f86' },
  { name: 'Teal', value: '#1c6a6a' },
  { name: 'Rose', value: '#9c3f5a' },
];

export const DEFAULT_PREFS: UserPrefs = {
  theme: 'system',
  accent: '#1f6f4a',
  semesterAccents: {},
  dismissedInstallPrompt: false,
  dismissedPushPrompt: false,
};

const HEX = /^#[0-9a-f]{6}$/i;

export function isValidAccent(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value);
}

/** Merge anything shaped roughly like prefs onto the defaults, safely. */
export function coercePrefs(raw: unknown): UserPrefs {
  const input = (raw ?? {}) as Partial<UserPrefs>;
  const theme: ThemePreference =
    input.theme === 'light' || input.theme === 'dark' || input.theme === 'system'
      ? input.theme
      : DEFAULT_PREFS.theme;

  const semesterAccents: Record<string, string> = {};
  if (input.semesterAccents && typeof input.semesterAccents === 'object') {
    for (const [key, value] of Object.entries(input.semesterAccents)) {
      if (isValidAccent(value)) semesterAccents[key] = value;
    }
  }

  return {
    theme,
    accent: isValidAccent(input.accent) ? input.accent : DEFAULT_PREFS.accent,
    semesterAccents,
    dismissedInstallPrompt: !!input.dismissedInstallPrompt,
    dismissedPushPrompt: !!input.dismissedPushPrompt,
  };
}

export function readCachedPrefs(): UserPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    return raw ? coercePrefs(JSON.parse(raw)) : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writeCachedPrefs(prefs: UserPrefs): void {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* storage full or blocked; the server copy still holds */
  }
}

export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Apply appearance to the document.
 *
 * `accentOverride` is how a semester paints its own interior: the same
 * mechanism as the user preference, one variable, so nothing downstream has to
 * know which of the two is in force.
 */
export function applyAppearance(prefs: UserPrefs, accentOverride?: string | null): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const resolved = resolveTheme(prefs.theme);
  root.dataset.theme = resolved;

  const accent = isValidAccent(accentOverride) ? accentOverride : prefs.accent;
  root.style.setProperty('--seed-accent', accent);

  // Keep the browser chrome — iOS status bar, Android address bar — in step
  // with the surface underneath it, or the app looks pasted onto the OS.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  const color = resolved === 'dark' ? '#131211' : '#f6f4f0';
  if (meta) {
    meta.content = color;
  } else {
    const el = document.createElement('meta');
    el.name = 'theme-color';
    el.content = color;
    document.head.appendChild(el);
  }
}

/**
 * Watch the OS setting, but only while the preference is "system".
 *
 * Returns an unsubscribe. Callers re-subscribe when the preference changes, so
 * a user who has explicitly chosen light does not get flipped at sunset.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/**
 * A readable foreground for an arbitrary background.
 *
 * Used for the small number of places that paint a swatch in a color the
 * design system did not choose — the accent picker, semester cover chips.
 * Relative luminance per WCAG, then the better of black or white.
 */
export function readableOn(hex: string): '#ffffff' | '#17150f' {
  if (!isValidAccent(hex)) return '#ffffff';
  const n = parseInt(hex.slice(1), 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  return luminance > 0.42 ? '#17150f' : '#ffffff';
}
